export type Pt = { x: number; y: number };
export type MeasureKind = 'line' | 'polyline' | 'rect' | 'area';
export type MeasureTool = 'pan' | MeasureKind | 'id';
export type Unit = 'u' | 'mm' | 'cm' | 'm' | 'km' | 'in' | 'ft';
export type SnapType = 'vertex' | 'midpoint' | 'intersection' | 'edge' | 'survey';
export type SnapHit = { x: number; y: number; type: SnapType };

// screen = { x: tx + k * doc.x, y: ty + k * doc.y }  (doc = SVG user units)
export type ViewMap = { tx: number; ty: number; k: number };

export type SvgScale = {
  unitsPerSvg: number;
  defaultUnit: Unit;
};

export type MeasureVM = {
  id: string;
  kind: MeasureKind;
  name: string;
  // Tab the measurement belongs to ('model' / 'layout:<name>'); null = made on
  // a legacy single-view file.
  layout: string | null;
  pts: Pt[];
  distance: number | null;
  area: number | null;
  unsaved?: boolean;
  // A saved measurement shown on a share link: guests cannot rename or delete it.
  readOnly?: boolean;
};

// ── Scale & units ─────────────────────────────────────────────────────────────

const INSUNITS_TO_UNIT: Record<string, Unit> = {
  '1': 'in', '2': 'ft', '4': 'mm', '5': 'cm', '6': 'm', '7': 'km',
};

export function parseViewBox(svgEl: SVGSVGElement): { x: number; y: number; w: number; h: number } | null {
  const vb = (svgEl.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  if (vb.length !== 4 || vb.some((n) => !Number.isFinite(n)) || vb[2] <= 0 || vb[3] <= 0) return null;
  return { x: vb[0], y: vb[1], w: vb[2], h: vb[3] };
}

// ezdxf normalises the viewBox to an abstract integer space; the physical size
// survives only in the root width/height ("100mm" = 100 drawing units in model
// mode, paper millimetres in sheet mode). unitsPerSvg = that ratio.
export function detectSvgScale(svgEl: SVGSVGElement, originalWidthAttr: string | null): SvgScale {
  const vb = parseViewBox(svgEl);
  let unitsPerSvg = 1;
  let widthBased = false;
  if (vb && originalWidthAttr) {
    const m = originalWidthAttr.trim().match(/^([\d.]+)\s*(mm)?$/);
    if (m) {
      const w = parseFloat(m[1]);
      if (Number.isFinite(w) && w > 0) {
        unitsPerSvg = w / vb.w;
        widthBased = true;
      }
    }
  }
  // When the converter declared a unit code, honour it — an unmapped or
  // unitless code means "unknown", not metres. Only SVGs from before the
  // data-insunits injection fall back to the metres guess.
  const ins = svgEl.getAttribute('data-insunits');
  const defaultUnit: Unit = ins !== null
    ? (INSUNITS_TO_UNIT[ins] ?? 'u')
    : (widthBased ? 'm' : 'u');
  return { unitsPerSvg, defaultUnit };
}

// ── Formatting ────────────────────────────────────────────────────────────────

const UNIT_TO_MM: Record<Exclude<Unit, 'u'>, number> = {
  mm: 1, cm: 10, m: 1000, km: 1e6, in: 25.4, ft: 304.8,
};

function fmtNum(v: number, maxDp: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: maxDp });
}

function adaptiveDp(v: number): number {
  const a = Math.abs(v);
  if (a >= 1000) return 1;
  if (a >= 100) return 2;
  return a >= 1 ? 2 : 3;
}

export function formatLength(valueDU: number, unit: Unit): string {
  if (unit === 'u') return `${fmtNum(valueDU, adaptiveDp(valueDU))} u`;
  const mm = valueDU * UNIT_TO_MM[unit];
  if (unit === 'in' || unit === 'ft') {
    const ft = mm / 304.8;
    if (unit === 'ft' || Math.abs(ft) >= 4) return `${fmtNum(ft, adaptiveDp(ft))} ft`;
    const inch = mm / 25.4;
    return `${fmtNum(inch, adaptiveDp(inch))} in`;
  }
  const am = Math.abs(mm);
  if (am >= 1e6) return `${fmtNum(mm / 1e6, 2)} km`;
  if (am >= 1000 || unit === 'm' || unit === 'km') return `${fmtNum(mm / 1000, adaptiveDp(mm / 1000))} m`;
  if (unit === 'cm') return `${fmtNum(mm / 10, adaptiveDp(mm / 10))} cm`;
  return `${fmtNum(mm, adaptiveDp(mm))} mm`;
}

export function formatArea(valueDU2: number, unit: Unit): string {
  if (unit === 'u') return `${fmtNum(valueDU2, adaptiveDp(valueDU2))} u²`;
  const f = UNIT_TO_MM[unit];
  const mm2 = valueDU2 * f * f;
  if (unit === 'in' || unit === 'ft') {
    const ft2 = mm2 / (304.8 * 304.8);
    if (unit === 'ft' || Math.abs(ft2) >= 10) return `${fmtNum(ft2, adaptiveDp(ft2))} ft²`;
    const in2 = mm2 / (25.4 * 25.4);
    return `${fmtNum(in2, adaptiveDp(in2))} in²`;
  }
  const am = Math.abs(mm2);
  if (am >= 1e12) return `${fmtNum(mm2 / 1e12, 3)} km²`;
  if (am >= 1e6 || unit === 'm' || unit === 'km') return `${fmtNum(mm2 / 1e6, adaptiveDp(mm2 / 1e6))} m²`;
  if (unit === 'cm') return `${fmtNum(mm2 / 100, adaptiveDp(mm2 / 100))} cm²`;
  return `${fmtNum(mm2, adaptiveDp(mm2))} mm²`;
}

// ── Geometry ──────────────────────────────────────────────────────────────────

export function dist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function polylineLength(pts: Pt[]): number {
  let s = 0;
  for (let i = 1; i < pts.length; i++) s += dist(pts[i - 1], pts[i]);
  return s;
}

export function polygonArea(pts: Pt[]): number {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
}

export function polygonCentroid(pts: Pt[]): Pt {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length];
    const cross = p.x * q.y - q.x * p.y;
    a += cross;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  }
  if (Math.abs(a) < 1e-12) {
    const n = pts.length || 1;
    return {
      x: pts.reduce((s, p) => s + p.x, 0) / n,
      y: pts.reduce((s, p) => s + p.y, 0) / n,
    };
  }
  return { x: cx / (3 * a), y: cy / (3 * a) };
}

export function bboxOfPts(pts: Pt[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function rectOutline(a: Pt, b: Pt): Pt[] {
  return [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
}

export function measureValues(kind: MeasureKind, pts: Pt[], unitsPerSvg: number): { distance: number | null; area: number | null } {
  const k = unitsPerSvg;
  if (kind === 'line' || kind === 'polyline') {
    return { distance: polylineLength(pts) * k, area: null };
  }
  if (kind === 'rect') {
    const o = rectOutline(pts[0], pts[1]);
    return { distance: polylineLength([...o, o[0]]) * k, area: polygonArea(o) * k * k };
  }
  return { distance: polylineLength([...pts, pts[0]]) * k, area: polygonArea(pts) * k * k };
}

function pointSegNearest(px: number, py: number, x1: number, y1: number, x2: number, y2: number): { x: number; y: number; d2: number } {
  const dx = x2 - x1, dy = y2 - y1;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - x1) * dx + (py - y1) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  const x = x1 + t * dx, y = y1 + t * dy;
  const ex = px - x, ey = py - y;
  return { x, y, d2: ex * ex + ey * ey };
}

function segSegIntersect(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number
): { x: number; y: number } | null {
  const r1x = bx - ax, r1y = by - ay;
  const r2x = dx - cx, r2y = dy - cy;
  const den = r1x * r2y - r1y * r2x;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((cx - ax) * r2y - (cy - ay) * r2x) / den;
  const u = ((cx - ax) * r1y - (cy - ay) * r1x) / den;
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
  return { x: ax + t * r1x, y: ay + t * r1y };
}

// ── Transform handling ────────────────────────────────────────────────────────

type Mat = [number, number, number, number, number, number];
const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

function matMul(m: Mat, n: Mat): Mat {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

function matApply(m: Mat, x: number, y: number): [number, number] {
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

const TRANSFORM_FN = /([a-zA-Z]+)\s*\(([^)]*)\)/g;

function parseTransformAttr(attr: string): Mat {
  let m: Mat = IDENTITY;
  let match: RegExpExecArray | null;
  TRANSFORM_FN.lastIndex = 0;
  while ((match = TRANSFORM_FN.exec(attr)) !== null) {
    const fn = match[1];
    const args = match[2].trim().split(/[\s,]+/).map(Number);
    let t: Mat | null = null;
    if (fn === 'matrix' && args.length === 6) t = args as Mat;
    else if (fn === 'translate') t = [1, 0, 0, 1, args[0] || 0, args[1] || 0];
    else if (fn === 'scale') t = [args[0] ?? 1, 0, 0, args.length > 1 ? args[1] : (args[0] ?? 1), 0, 0];
    else if (fn === 'rotate') {
      const a = ((args[0] || 0) * Math.PI) / 180;
      const cos = Math.cos(a), sin = Math.sin(a);
      const rot: Mat = [cos, sin, -sin, cos, 0, 0];
      if (args.length >= 3) {
        t = matMul(matMul([1, 0, 0, 1, args[1], args[2]], rot), [1, 0, 0, 1, -args[1], -args[2]]);
      } else t = rot;
    }
    if (t) m = matMul(m, t);
  }
  return m;
}

// Nested <svg> (stacked paper sheets) remaps its viewBox into the x/y/width/
// height rect using the default preserveAspectRatio (xMidYMid meet).
function nestedSvgMat(el: SVGSVGElement): Mat {
  const vb = parseViewBox(el);
  if (!vb) return IDENTITY;
  const x = parseFloat(el.getAttribute('x') || '0') || 0;
  const y = parseFloat(el.getAttribute('y') || '0') || 0;
  const w = parseFloat(el.getAttribute('width') || '') || vb.w;
  const h = parseFloat(el.getAttribute('height') || '') || vb.h;
  const s = Math.min(w / vb.w, h / vb.h);
  const tx = x + (w - vb.w * s) / 2 - vb.x * s;
  const ty = y + (h - vb.h * s) / 2 - vb.y * s;
  return [s, 0, 0, s, tx, ty];
}

// ── Snap index ────────────────────────────────────────────────────────────────

const GRID_DIM = 256;

export type SnapIndex = {
  segs: number[];        // x1,y1,x2,y2 per segment
  segReal: number[];     // 1 = straight drawing segment (midpoint-eligible)
  verts: number[];       // x,y per vertex
  cells: Map<number, { s: number[]; v: number[] }>;
  minX: number; minY: number; cellW: number; cellH: number;
  flatStep: number;      // target chord length for curve flattening
  segCount: number;
  vertCount: number;
  truncated: boolean;    // size cap hit — snap covers only part of the drawing
};

type BuilderState = {
  index: SnapIndex;
  els: Element[];
  next: number;
  done: boolean;
};

const NUM_OR_CMD = /[MmLlHhVvCcSsQqTtAaZz]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g;

function cellOf(index: SnapIndex, x: number, y: number): [number, number] {
  let cx = Math.floor((x - index.minX) / index.cellW);
  let cy = Math.floor((y - index.minY) / index.cellH);
  cx = Math.max(0, Math.min(GRID_DIM - 1, cx));
  cy = Math.max(0, Math.min(GRID_DIM - 1, cy));
  return [cx, cy];
}

function cellBucket(index: SnapIndex, cx: number, cy: number): { s: number[]; v: number[] } {
  const key = cy * GRID_DIM + cx;
  let b = index.cells.get(key);
  if (!b) {
    b = { s: [], v: [] };
    index.cells.set(key, b);
  }
  return b;
}

function addVertex(index: SnapIndex, x: number, y: number): void {
  const i = index.vertCount;
  index.verts.push(x, y);
  index.vertCount++;
  const [cx, cy] = cellOf(index, x, y);
  cellBucket(index, cx, cy).v.push(i);
}

// Grid walk along the segment (not its bbox) so a long diagonal touches only
// the cells it actually crosses.
function addSegment(index: SnapIndex, x1: number, y1: number, x2: number, y2: number, real: boolean): void {
  const i = index.segCount;
  index.segs.push(x1, y1, x2, y2);
  index.segReal.push(real ? 1 : 0);
  index.segCount++;

  let [cx, cy] = cellOf(index, x1, y1);
  const [ex, ey] = cellOf(index, x2, y2);
  cellBucket(index, cx, cy).s.push(i);
  const stepX = ex > cx ? 1 : -1;
  const stepY = ey > cy ? 1 : -1;
  const dx = x2 - x1, dy = y2 - y1;
  let guard = 2 * GRID_DIM + 4;
  while ((cx !== ex || cy !== ey) && guard-- > 0) {
    if (cx !== ex && cy !== ey) {
      const bx = index.minX + (cx + (stepX > 0 ? 1 : 0)) * index.cellW;
      const by = index.minY + (cy + (stepY > 0 ? 1 : 0)) * index.cellH;
      const tX = dx !== 0 ? (bx - x1) / dx : Infinity;
      const tY = dy !== 0 ? (by - y1) / dy : Infinity;
      if (tX <= tY) cx += stepX; else cy += stepY;
    } else if (cx !== ex) cx += stepX;
    else cy += stepY;
    cellBucket(index, cx, cy).s.push(i);
  }
}

// Subdivision scaled to the curve's size so big arcs stay within a sub-pixel
// sagitta at any reasonable zoom, while glyph-sized curves stay cheap.
function curveSteps(index: SnapIndex, approxLen: number): number {
  return Math.max(6, Math.min(32, Math.ceil(approxLen / index.flatStep)));
}

function flattenCubic(index: SnapIndex, x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): [number, number] {
  const n = curveSteps(index, Math.hypot(x1 - x0, y1 - y0) + Math.hypot(x2 - x1, y2 - y1) + Math.hypot(x3 - x2, y3 - y2));
  let px = x0, py = y0;
  for (let i = 1; i <= n; i++) {
    const t = i / n, mt = 1 - t;
    const x = mt * mt * mt * x0 + 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t * x3;
    const y = mt * mt * mt * y0 + 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t * y3;
    addSegment(index, px, py, x, y, false);
    px = x; py = y;
  }
  return [px, py];
}

function flattenQuad(index: SnapIndex, x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): [number, number] {
  const n = curveSteps(index, Math.hypot(x1 - x0, y1 - y0) + Math.hypot(x2 - x1, y2 - y1));
  let px = x0, py = y0;
  for (let i = 1; i <= n; i++) {
    const t = i / n, mt = 1 - t;
    const x = mt * mt * x0 + 2 * mt * t * x1 + t * t * x2;
    const y = mt * mt * y0 + 2 * mt * t * y1 + t * t * y2;
    addSegment(index, px, py, x, y, false);
    px = x; py = y;
  }
  return [px, py];
}

function parsePathInto(index: SnapIndex, d: string, mat: Mat): void {
  NUM_OR_CMD.lastIndex = 0;
  const tokens = d.match(NUM_OR_CMD);
  if (!tokens) return;

  let i = 0;
  let cmd = '';
  let cx = 0, cy = 0;        // current point (path space)
  let sx = 0, sy = 0;        // subpath start
  let pcx = 0, pcy = 0;      // previous control point for S/T
  let prevCmd = '';
  let started = false;

  const num = (): number => parseFloat(tokens[i++]);
  const emitVertex = (x: number, y: number): void => {
    const [tx, ty] = matApply(mat, x, y);
    addVertex(index, tx, ty);
  };
  const emitLine = (x1: number, y1: number, x2: number, y2: number, real: boolean): void => {
    const [ax, ay] = matApply(mat, x1, y1);
    const [bx, by] = matApply(mat, x2, y2);
    addSegment(index, ax, ay, bx, by, real);
  };
  const emitCubic = (x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): void => {
    const [p0x, p0y] = matApply(mat, cx, cy);
    const [p1x, p1y] = matApply(mat, x1, y1);
    const [p2x, p2y] = matApply(mat, x2, y2);
    const [p3x, p3y] = matApply(mat, x3, y3);
    flattenCubic(index, p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y);
  };
  const emitQuad = (x1: number, y1: number, x2: number, y2: number): void => {
    const [p0x, p0y] = matApply(mat, cx, cy);
    const [p1x, p1y] = matApply(mat, x1, y1);
    const [p2x, p2y] = matApply(mat, x2, y2);
    flattenQuad(index, p0x, p0y, p1x, p1y, p2x, p2y);
  };

  while (i < tokens.length) {
    const iterStart = i;
    const tok = tokens[i];
    if (/^[a-zA-Z]$/.test(tok)) {
      cmd = tok;
      i++;
    } else if (cmd === 'M') cmd = 'L';
    else if (cmd === 'm') cmd = 'l';

    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();

    switch (C) {
      case 'M': {
        const x = num(), y = num();
        cx = rel && started ? cx + x : x;
        cy = rel && started ? cy + y : y;
        sx = cx; sy = cy;
        started = true;
        emitVertex(cx, cy);
        break;
      }
      case 'L': {
        const x = num(), y = num();
        const nx = rel ? cx + x : x;
        const ny = rel ? cy + y : y;
        emitLine(cx, cy, nx, ny, true);
        cx = nx; cy = ny;
        emitVertex(cx, cy);
        break;
      }
      case 'H': {
        const x = num();
        const nx = rel ? cx + x : x;
        emitLine(cx, cy, nx, cy, true);
        cx = nx;
        emitVertex(cx, cy);
        break;
      }
      case 'V': {
        const y = num();
        const ny = rel ? cy + y : y;
        emitLine(cx, cy, cx, ny, true);
        cy = ny;
        emitVertex(cx, cy);
        break;
      }
      case 'C': {
        let x1 = num(), y1 = num(), x2 = num(), y2 = num(), x = num(), y = num();
        if (rel) { x1 += cx; y1 += cy; x2 += cx; y2 += cy; x += cx; y += cy; }
        emitCubic(x1, y1, x2, y2, x, y);
        pcx = x2; pcy = y2;
        cx = x; cy = y;
        emitVertex(cx, cy);
        break;
      }
      case 'S': {
        let x2 = num(), y2 = num(), x = num(), y = num();
        if (rel) { x2 += cx; y2 += cy; x += cx; y += cy; }
        const refl = /^[CcSs]$/.test(prevCmd);
        const x1 = refl ? 2 * cx - pcx : cx;
        const y1 = refl ? 2 * cy - pcy : cy;
        emitCubic(x1, y1, x2, y2, x, y);
        pcx = x2; pcy = y2;
        cx = x; cy = y;
        emitVertex(cx, cy);
        break;
      }
      case 'Q': {
        let x1 = num(), y1 = num(), x = num(), y = num();
        if (rel) { x1 += cx; y1 += cy; x += cx; y += cy; }
        emitQuad(x1, y1, x, y);
        pcx = x1; pcy = y1;
        cx = x; cy = y;
        emitVertex(cx, cy);
        break;
      }
      case 'T': {
        let x = num(), y = num();
        if (rel) { x += cx; y += cy; }
        const refl = /^[QqTt]$/.test(prevCmd);
        const x1 = refl ? 2 * cx - pcx : cx;
        const y1 = refl ? 2 * cy - pcy : cy;
        emitQuad(x1, y1, x, y);
        pcx = x1; pcy = y1;
        cx = x; cy = y;
        emitVertex(cx, cy);
        break;
      }
      case 'A': {
        // Arc flattening via straight chord sampling of the endpoint — rare in
        // ezdxf output; the chord keeps the endpoints exact for snapping.
        num(); num(); num(); num(); num();
        const x = num(), y = num();
        const nx = rel ? cx + x : x;
        const ny = rel ? cy + y : y;
        emitLine(cx, cy, nx, ny, false);
        cx = nx; cy = ny;
        emitVertex(cx, cy);
        break;
      }
      case 'Z': {
        if (cx !== sx || cy !== sy) emitLine(cx, cy, sx, sy, true);
        cx = sx; cy = sy;
        break;
      }
      default:
        i++;
        break;
    }
    prevCmd = cmd;
    if (C === 'M') cmd = rel ? 'l' : 'L';
    if (i === iterStart) i++;
  }
}

function strokeClassSet(root: SVGSVGElement): Set<string> {
  const strokes = new Set<string>();
  const styleRe = /\.([A-Za-z0-9_-]+)\s*\{([^}]*)\}/g;
  root.querySelectorAll('style').forEach((st) => {
    const css = st.textContent || '';
    let m: RegExpExecArray | null;
    styleRe.lastIndex = 0;
    while ((m = styleRe.exec(css)) !== null) {
      const body = m[2];
      const sm = body.match(/(?:^|;)\s*stroke\s*:\s*([^;]+)/);
      if (sm && sm[1].trim() !== 'none') strokes.add(m[1]);
    }
  });
  return strokes;
}

const MAX_INDEX_SEGS = 1_200_000;
const MAX_PATH_D_CHARS = 1_000_000;

export function createSnapIndexBuilder(root: SVGSVGElement): {
  step: (budgetMs: number) => boolean;
  progress: () => number;
  index: SnapIndex;
} {
  const vb = parseViewBox(root) || { x: 0, y: 0, w: 1, h: 1 };
  const index: SnapIndex = {
    segs: [], segReal: [], verts: [],
    cells: new Map(),
    minX: vb.x, minY: vb.y,
    cellW: Math.max(vb.w / GRID_DIM, 1e-9),
    cellH: Math.max(vb.h / GRID_DIM, 1e-9),
    flatStep: Math.max(vb.w, vb.h) / 4096,
    segCount: 0, vertCount: 0,
    truncated: false,
  };

  // Text glyphs are filled outlines (stroke: none) — snapping to letter shapes
  // is pure noise, so only stroked classes take part.
  const strokeClasses = strokeClassSet(root);

  const els: Element[] = [];
  const matCache = new Map<Element, Mat>();
  matCache.set(root, IDENTITY);

  const walk = (el: Element): void => {
    for (const child of Array.from(el.children)) {
      const tag = child.tagName.toLowerCase();
      if (tag === 'defs' || tag === 'clippath' || tag === 'mask' || tag === 'symbol' || tag === 'style') continue;
      const parentMat = matCache.get(el) || IDENTITY;
      let mat = parentMat;
      if (tag === 'svg') mat = matMul(parentMat, nestedSvgMat(child as SVGSVGElement));
      const tr = child.getAttribute('transform');
      if (tr) mat = matMul(mat, parseTransformAttr(tr));
      matCache.set(child, mat);
      if (tag === 'path') {
        const cls = child.getAttribute('class');
        const strokeAttr = child.getAttribute('stroke');
        const included = cls
          ? cls.split(/\s+/).some((c) => strokeClasses.has(c))
          : strokeAttr !== 'none';
        if (included) els.push(child);
      } else if (tag !== 'rect' && tag !== 'text' && tag !== 'image') {
        walk(child);
      }
    }
  };
  walk(root);

  const state: BuilderState = { index, els, next: 0, done: els.length === 0 };

  return {
    index,
    progress: () => (state.els.length === 0 ? 1 : state.next / state.els.length),
    // Time-boxed, not element-boxed: hatch fills arrive as single megabyte-scale
    // paths, so a fixed element count could freeze the UI for seconds.
    step: (budgetMs: number): boolean => {
      if (state.done) return true;
      const t0 = performance.now();
      while (state.next < state.els.length) {
        if (index.segCount >= MAX_INDEX_SEGS) {
          index.truncated = true;
          state.next = state.els.length;
          break;
        }
        const el = state.els[state.next];
        state.next++;
        const d = el.getAttribute('d');
        if (d) {
          if (d.length > MAX_PATH_D_CHARS) {
            index.truncated = true;
          } else {
            parsePathInto(index, d, matCache.get(el) || IDENTITY);
          }
        }
        if (performance.now() - t0 >= budgetMs) break;
      }
      if (state.next >= state.els.length) state.done = true;
      return state.done;
    },
  };
}

// ── Snap query ────────────────────────────────────────────────────────────────

const MAX_SCAN_RADIUS_CELLS = 10;
const MAX_INTERSECT_SEGS = 64;

export function snapAt(index: SnapIndex, p: Pt, tolDoc: number): SnapHit | null {
  const [pcx, pcy] = cellOf(index, p.x, p.y);
  const rx = Math.min(MAX_SCAN_RADIUS_CELLS, Math.ceil(tolDoc / index.cellW));
  const ry = Math.min(MAX_SCAN_RADIUS_CELLS, Math.ceil(tolDoc / index.cellH));

  const segSet = new Set<number>();
  const vertSet = new Set<number>();
  for (let cy = Math.max(0, pcy - ry); cy <= Math.min(GRID_DIM - 1, pcy + ry); cy++) {
    for (let cx = Math.max(0, pcx - rx); cx <= Math.min(GRID_DIM - 1, pcx + rx); cx++) {
      const b = index.cells.get(cy * GRID_DIM + cx);
      if (!b) continue;
      for (const s of b.s) segSet.add(s);
      for (const v of b.v) vertSet.add(v);
    }
  }

  const tol2 = tolDoc * tolDoc;

  let best: SnapHit | null = null;
  let bestD2 = tol2;
  for (const vi of vertSet) {
    const x = index.verts[vi * 2], y = index.verts[vi * 2 + 1];
    const dx = x - p.x, dy = y - p.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = { x, y, type: 'vertex' };
    }
  }
  if (best) return best;

  const midTol2 = tol2 * 0.72;
  bestD2 = midTol2;
  for (const si of segSet) {
    if (!index.segReal[si]) continue;
    const o = si * 4;
    const mx = (index.segs[o] + index.segs[o + 2]) / 2;
    const my = (index.segs[o + 1] + index.segs[o + 3]) / 2;
    const dx = mx - p.x, dy = my - p.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = { x: mx, y: my, type: 'midpoint' };
    }
  }
  if (best) return best;

  const segList = Array.from(segSet);
  if (segList.length <= MAX_INTERSECT_SEGS) {
    bestD2 = midTol2;
    for (let a = 0; a < segList.length; a++) {
      const oa = segList[a] * 4;
      for (let b = a + 1; b < segList.length; b++) {
        const ob = segList[b] * 4;
        const hit = segSegIntersect(
          index.segs[oa], index.segs[oa + 1], index.segs[oa + 2], index.segs[oa + 3],
          index.segs[ob], index.segs[ob + 1], index.segs[ob + 2], index.segs[ob + 3]
        );
        if (!hit) continue;
        const dx = hit.x - p.x, dy = hit.y - p.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD2) {
          bestD2 = d2;
          best = { x: hit.x, y: hit.y, type: 'intersection' };
        }
      }
    }
    if (best) return best;
  }

  bestD2 = tol2 * 0.49;
  for (const si of segSet) {
    const o = si * 4;
    const n = pointSegNearest(p.x, p.y, index.segs[o], index.segs[o + 1], index.segs[o + 2], index.segs[o + 3]);
    if (n.d2 < bestD2) {
      bestD2 = n.d2;
      best = { x: n.x, y: n.y, type: 'edge' };
    }
  }
  return best;
}

export function orthoConstrain(prev: Pt, cursor: Pt): Pt {
  const dx = cursor.x - prev.x, dy = cursor.y - prev.y;
  return Math.abs(dx) >= Math.abs(dy)
    ? { x: cursor.x, y: prev.y }
    : { x: prev.x, y: cursor.y };
}
